import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PurpleNavBar } from '../components/PurpleNavBar';
import { FileUpload } from '../components/FileUpload';
import { COLORS } from '../../../core/constants/colors';
import { Document, Company } from '../types';
import { api } from '../../../core/utils/apiClient';

export const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (companyParam) {
      setSelectedCompany(parseInt(companyParam));
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load only the partner's own companies (not the whole portfolio)
      const companiesResponse = await api.get('/api/v1/companies/?company_mode=true');

      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        setCompanies(companiesData.companies || []);

        // Load documents for selected company
        if (selectedCompany) {
          const docsResponse = await api.get(
            `/api/v1/documents/companies/${selectedCompany}/documents`
          );

          if (docsResponse.ok) {
            const docsData = await docsResponse.json();
            setDocuments(docsData.documents || []);
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompany(companyId);
    navigate(`/dashboard/documents?company=${companyId}`);
  };

  const handleUploadComplete = (documentId: number) => {
    loadData();
    setShowUpload(false);
  };

  const handleDownload = async (documentId: number) => {
    try {
      const response = await api.get(
        `/api/v1/documents/documents/${documentId}/download`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document';
        a.click();
      }
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('idToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

      const response = await fetch(`${API_BASE_URL}/api/v1/documents/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.original_file_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return COLORS.primary.softLime;
      case 'processing':
        return COLORS.extended.orange;
      case 'failed':
        return COLORS.extended.redSalsa;
      default:
        return COLORS.extended.lavenderBlue;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
      <PurpleNavBar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Document Management
            </h1>
            <p style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
              Upload and manage portfolio company documents
            </p>
          </div>
          {selectedCompany && (
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:shadow-lg"
              style={{ backgroundColor: COLORS.extended.orange, color: '#FFFFFF' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              {showUpload ? 'Hide Upload' : 'Upload Documents'}
            </button>
          )}
        </div>

        {/* Company Selection */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
            Select Company
          </label>
          <select
            value={selectedCompany || ''}
            onChange={(e) => handleCompanyChange(parseInt(e.target.value))}
            className="w-full md:w-auto px-4 py-2 rounded-lg border-2 focus:outline-none focus:ring-2"
            style={{
              borderColor: COLORS.extended.cultured,
              color: COLORS.primary.persianIndigo,
            }}
          >
            <option value="">Select a company...</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCompany ? (
          <>
            {/* Upload Section */}
            {showUpload && (
              <div className="mb-8">
                <FileUpload
                  companyId={selectedCompany}
                  onUploadComplete={handleUploadComplete}
                  onUploadError={(error) => console.error('Upload error:', error)}
                />
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                      style={{ color: COLORS.primary.persianIndigo, opacity: 0.5 }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: COLORS.extended.cultured,
                        color: COLORS.primary.persianIndigo,
                      }}
                    />
                  </div>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    borderColor: COLORS.extended.cultured,
                    color: COLORS.primary.persianIndigo,
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="uploaded">Uploaded</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            {/* Documents List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div
                    className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
                    style={{
                      borderColor: COLORS.user.primary,
                      borderTopColor: 'transparent',
                    }}
                  />
                  <p style={{ color: COLORS.primary.persianIndigo }}>Loading documents...</p>
                </div>
              </div>
            ) : filteredDocuments.length > 0 ? (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ backgroundColor: COLORS.extended.cultured }}>
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Document
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Type
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Size
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Uploaded
                        </th>
                        <th className="px-6 py-4 text-right text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((doc, index) => (
                        <tr
                          key={doc.id}
                          className="border-b hover:bg-opacity-50"
                          style={{
                            borderColor: COLORS.extended.cultured,
                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : COLORS.extended.cultured + '30',
                          }}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: COLORS.user.primary + '20' }}
                              >
                                <svg className="w-5 h-5" style={{ color: COLORS.user.primary }} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate" style={{ color: COLORS.primary.persianIndigo }}>
                                  {doc.original_file_name}
                                </p>
                                {doc.document_type && (
                                  <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                                    {doc.document_type}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: COLORS.primary.persianIndigo }}>
                              {doc.file_type.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: COLORS.primary.persianIndigo }}>
                              {formatFileSize(doc.file_size)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                              style={{
                                backgroundColor: getStatusColor(doc.status) + '30',
                                color: COLORS.primary.persianIndigo,
                              }}
                            >
                              {doc.status === 'processing' && (
                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              )}
                              {doc.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                              {new Date(doc.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleDownload(doc.id)}
                                className="p-2 rounded-lg hover:bg-opacity-10 transition-all"
                                style={{ color: COLORS.user.queryButton }}
                                title="Download"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(doc.id)}
                                className="p-2 rounded-lg hover:bg-opacity-10 transition-all"
                                style={{ color: COLORS.extended.redSalsa }}
                                title="Delete"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <svg
                  className="w-16 h-16 mx-auto mb-4"
                  style={{ color: COLORS.primary.persianIndigo, opacity: 0.3 }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                  No documents yet
                </h3>
                <p className="text-sm mb-6" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                  Upload your first document to get started
                </p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="px-6 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: COLORS.user.queryButton, color: '#FFFFFF' }}
                >
                  Upload Document
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <svg
              className="w-16 h-16 mx-auto mb-4"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.3 }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Select a company
            </h3>
            <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
              Choose a company from the dropdown above to manage its documents
            </p>
          </div>
        )}
      </main>
    </div>
  );
};